import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, Thermometer, Info, Sun, Moon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Slider } from '../../../components/ui/slider';
import { Badge } from '../../../components/ui/badge';
import { SCIENCE_THEME_KEY } from '../Class9SciencePage';

/**
 * Class 9 NCERT Science · Chapter 1 — Matter In Our Surroundings
 * Interactive simulator: drag temperature slider and watch molecules transition
 * between solid → liquid → gas states.
 *
 * Visualisation: Canvas 2D with N particles. Behaviour depends on temperature (T in °C):
 *   T < 0  → SOLID  : particles vibrate in fixed lattice
 *   0–100  → LIQUID : particles flow with moderate kinetic energy
 *   > 100  → GAS    : particles move freely & fast, large gaps
 */

const W = 720;
const H = 420;
const N_PARTICLES = 80;

const MatterStatesSimulation = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const [temperature, setTemperature] = useState(25); // °C
  const [running, setRunning] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem(SCIENCE_THEME_KEY) || 'light');
  useEffect(() => { localStorage.setItem(SCIENCE_THEME_KEY, theme); }, [theme]);
  const isLight = theme === 'light';
  const cls = useMemo(() => ({
    page:    isLight ? 'bg-gray-50 text-gray-900' : 'bg-[#0A0F1C] text-white',
    topbar:  isLight ? 'border-gray-200 bg-white' : 'border-white/5',
    card:    isLight ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#0F1626] border-white/10',
    mutedText: isLight ? 'text-gray-500' : 'text-gray-500',
    softText:  isLight ? 'text-gray-700' : 'text-gray-400',
    label:     isLight ? 'text-gray-400' : 'text-gray-600',
    backBtn:   isLight ? 'text-gray-600 hover:text-teal-600' : 'text-gray-400 hover:text-teal-400',
    btnOutline: isLight ? 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700' : 'border-white/10 bg-white/[0.02] hover:bg-white/5',
  }), [isLight]);

  // Initialise particles in lattice positions
  const initParticles = () => {
    const cols = 10;
    const rows = 8;
    const spacingX = W / (cols + 1);
    const spacingY = H / (rows + 1);
    const arr = [];
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const homeX = spacingX * (j + 1);
        const homeY = spacingY * (i + 1);
        arr.push({
          x: homeX,
          y: homeY,
          homeX,
          homeY,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
        });
      }
    }
    return arr;
  };

  useEffect(() => {
    particlesRef.current = initParticles();
  }, []);

  // Derived state
  const phase = temperature < 0 ? 'solid' : temperature <= 100 ? 'liquid' : 'gas';
  const phaseConfig = {
    solid: {
      label: 'Solid',
      color: '#60a5fa',
      bg: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      description: 'Particles vibrate in fixed positions. Strong intermolecular forces hold them in a rigid lattice. Definite shape & volume.',
      examples: 'Ice, iron, salt',
    },
    liquid: {
      label: 'Liquid',
      color: '#14b8a6',
      bg: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
      description: 'Particles slide past each other freely. Moderate intermolecular forces. Definite volume but no fixed shape.',
      examples: 'Water, milk, oil',
    },
    gas: {
      label: 'Gas',
      color: '#f59e0b',
      bg: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
      description: 'Particles move rapidly in random directions with large gaps. Weak intermolecular forces. No fixed shape or volume.',
      examples: 'Air, steam, oxygen',
    },
  };
  const cfg = phaseConfig[phase];

  // Animation loop
  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frame = 0;

    const loop = () => {
      // Background gradient based on temperature
      const tNorm = Math.max(0, Math.min(1, (temperature + 50) / 200));
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, `rgba(15, 22, 38, 1)`);
      grad.addColorStop(1, `rgba(${Math.floor(20 + tNorm * 60)}, ${Math.floor(20 + tNorm * 30)}, ${Math.floor(50 - tNorm * 30)}, 1)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Container outline
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, W - 40, H - 40);

      // Energy factor — solids vibrate tiny, gas flies, liquid in between
      let energy;
      if (phase === 'solid') energy = 0.4 + (temperature + 50) / 250; // vibration amplitude
      else if (phase === 'liquid') energy = 1.5 + temperature / 80;
      else energy = 4 + (temperature - 100) / 50;

      // Update + draw particles
      particlesRef.current.forEach((p) => {
        if (phase === 'solid') {
          // Vibrate around home position
          p.x = p.homeX + (Math.random() - 0.5) * energy * 3;
          p.y = p.homeY + (Math.random() - 0.5) * energy * 3;
        } else {
          // Apply velocity with thermal kick
          p.vx += (Math.random() - 0.5) * energy * 0.2;
          p.vy += (Math.random() - 0.5) * energy * 0.2;
          // Cap velocity
          const maxV = energy * 1.5;
          p.vx = Math.max(-maxV, Math.min(maxV, p.vx));
          p.vy = Math.max(-maxV, Math.min(maxV, p.vy));
          p.x += p.vx;
          p.y += p.vy;
          // Bounce off walls
          if (p.x < 30) { p.x = 30; p.vx *= -0.85; }
          if (p.x > W - 30) { p.x = W - 30; p.vx *= -0.85; }
          if (p.y < 30) { p.y = 30; p.vy *= -0.85; }
          if (p.y > H - 30) { p.y = H - 30; p.vy *= -0.85; }
          // For liquid, gentle gravity-like settle
          if (phase === 'liquid') p.vy += 0.05;
        }

        // Draw
        ctx.beginPath();
        ctx.arc(p.x, p.y, phase === 'gas' ? 4 : 6, 0, Math.PI * 2);
        ctx.fillStyle = cfg.color;
        ctx.fill();
        // Subtle glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, phase === 'gas' ? 8 : 10, 0, Math.PI * 2);
        ctx.fillStyle = `${cfg.color}33`;
        ctx.fill();
      });

      // Thermometer overlay (top-left)
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = 'bold 18px sans-serif';
      ctx.fillText(`${temperature}°C`, 32, 48);
      ctx.font = '12px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillText(`${cfg.label.toUpperCase()} STATE`, 32, 65);

      frame++;
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, [running, temperature, phase, cfg.color, cfg.label]);

  const reset = () => {
    particlesRef.current = initParticles();
    setTemperature(25);
  };

  return (
    <div className={`min-h-screen ${cls.page}`} data-testid="sim-matter-states">
      {/* Top bar */}
      <div className={`border-b ${cls.topbar} px-6 py-4 flex items-center justify-between`}>
        <button onClick={() => navigate('/science/class-9')} className={`inline-flex items-center gap-2 text-sm ${cls.backBtn}`}>
          <ArrowLeft className="w-4 h-4" /> Back to Class 9 Chapters
        </button>
        <div className="flex items-center gap-3">
          <Badge className={`${isLight ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'} text-xs`}>CBSE · Class 9 · Chapter 1</Badge>
          <button
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            className={`p-2 rounded-lg ${isLight ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-white/5 text-gray-400'} transition-colors`}
            title={`Switch to ${isLight ? 'dark' : 'light'} mode`}
            data-testid="theme-toggle"
          >
            {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold mb-2">Matter In Our Surroundings</h1>
        <p className={`text-sm ${cls.softText} mb-8 max-w-2xl`}>
          Drag the temperature slider below. Watch how the molecules behave differently in solid, liquid, and gas states.
        </p>

        {/* Canvas + Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Canvas */}
          <div className={`${cls.card} border rounded-2xl p-4`}>
            <canvas
              ref={canvasRef}
              width={W}
              height={H}
              className="w-full h-auto bg-[#0A0F1C] rounded-lg"
              data-testid="sim-canvas"
            />
            <div className="mt-4 flex items-center justify-between gap-3">
              <Button onClick={() => setRunning(!running)} variant="outline" className={cls.btnOutline} data-testid="play-pause">
                {running ? <><Pause className="w-4 h-4 mr-2" />Pause</> : <><Play className="w-4 h-4 mr-2" />Play</>}
              </Button>
              <Button onClick={reset} variant="outline" className={cls.btnOutline} data-testid="reset">
                <RotateCcw className="w-4 h-4 mr-2" />Reset
              </Button>
            </div>
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            {/* Temperature control */}
            <div className={`${cls.card} border rounded-2xl p-5`}>
              <div className="flex items-center gap-2 mb-3">
                <Thermometer className="w-4 h-4 text-amber-500" />
                <p className="text-sm font-semibold">Temperature</p>
                <span className="ml-auto text-lg font-bold text-amber-500">{temperature}°C</span>
              </div>
              <Slider
                value={[temperature]}
                onValueChange={(v) => setTemperature(v[0])}
                min={-50}
                max={200}
                step={1}
                className="my-3"
                data-testid="temp-slider"
              />
              <div className={`flex justify-between text-[10px] ${cls.label} uppercase tracking-wider`}>
                <span>-50°C</span>
                <span>0°C ice</span>
                <span>100°C boil</span>
                <span>200°C</span>
              </div>
            </div>

            {/* Phase info */}
            <div className={`border rounded-2xl p-5 ${cfg.bg}`} data-testid="phase-info">
              <p className="text-[11px] uppercase tracking-wider mb-1 opacity-80">Current state</p>
              <h3 className="text-2xl font-bold mb-2">{cfg.label}</h3>
              <p className="text-xs leading-relaxed opacity-80 mb-3">{cfg.description}</p>
              <div className="pt-3 border-t border-current/10">
                <p className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Real-world examples</p>
                <p className="text-xs">{cfg.examples}</p>
              </div>
            </div>

            {/* Try this */}
            <div className={`${cls.card} border ${isLight ? 'border-teal-300' : 'border-teal-500/30'} rounded-2xl p-5`}>
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-teal-500" />
                <p className="text-sm font-semibold text-teal-600 dark:text-teal-400">Try this</p>
              </div>
              <ul className={`text-xs ${cls.softText} space-y-1.5 list-decimal list-inside`}>
                <li>Set to <span className="text-blue-500 font-medium">-20°C</span> — ice forms</li>
                <li>Slide to <span className="text-teal-500 font-medium">50°C</span> — watch it flow</li>
                <li>Push past <span className="text-amber-500 font-medium">100°C</span> — boils to gas</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Concept summary */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(phaseConfig).map(([k, v]) => (
            <div key={k} className={`border rounded-2xl p-4 ${k === phase ? v.bg : `${cls.card}`}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full" style={{ background: v.color }}></span>
                <p className="font-semibold">{v.label}</p>
              </div>
              <p className={`text-xs ${k === phase ? '' : cls.softText} leading-relaxed`}>{v.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MatterStatesSimulation;
